<script setup>
import { ref, computed, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import samlSettingsAPI from 'dashboard/api/samlSettings';
import customRoleAPI from 'dashboard/api/customRole';

import SectionLayout from '../../account/components/SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import TextInput from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'next/button/Button.vue';
import SamlInfoSection from './SamlInfoSection.vue';
import SamlAttributeMap from './SamlAttributeMap.vue';

const { t } = useI18n();

const id = ref(null);
const acsUrl = ref('');
const loadFailed = ref(false);
const customRoles = ref([]);
const roleMappings = ref([]);
const fingerprint = ref('');
const spEntityId = ref('');
const isEnabled = ref(false);
const isSubmitting = ref(false);
const isLoading = ref(true);
let loadInFlight = false;

const formState = reactive({
  ssoUrl: '',
  certificate: '',
  idpEntityId: '',
});

const validations = {
  ssoUrl: { required },
  certificate: { required },
  idpEntityId: { required },
};

const v$ = useVuelidate(validations, formState);


const ssoUrlError = computed(() =>
  v$.value.ssoUrl.$error
    ? t('SECURITY_SETTINGS.SAML.VALIDATION.SSO_URL_ERROR')
    : ''
);

const certificateError = computed(() =>
  v$.value.certificate.$error
    ? t('SECURITY_SETTINGS.SAML.VALIDATION.CERTIFICATE_ERROR')
    : ''
);

const idpEntityIdError = computed(() =>
  v$.value.idpEntityId.$error
    ? t('SECURITY_SETTINGS.SAML.VALIDATION.IDP_ENTITY_ID_ERROR')
    : ''
);

const loadSamlSettings = async () => {
  if (loadInFlight || isSubmitting.value) return;
  loadInFlight = true;
  try {
    isLoading.value = true;
    const [response, roles] = await Promise.all([samlSettingsAPI.get(), customRoleAPI.get()]);
    const settings = response.data;
    id.value = settings.id || null;
    formState.ssoUrl = settings.sso_url || '';
    formState.certificate = settings.certificate || '';
    formState.idpEntityId = settings.idp_entity_id || '';
    spEntityId.value = settings.sp_entity_id || '';
    acsUrl.value = settings.acs_url || '';
    fingerprint.value = settings.fingerprint || '';
    customRoles.value = roles.data;
    roleMappings.value = Object.entries(settings.role_mappings || {}).map(([group, mapping]) => ({
      group,
      target: mapping.custom_role_id ? `custom:${mapping.custom_role_id}` : mapping.role,
    }));
    isEnabled.value = !!settings.id;
    loadFailed.value = false;
  } catch {
    loadFailed.value = true;
    useAlert(t('SECURITY_SETTINGS.SAML.API.ERROR_LOADING'));
  } finally {
    isLoading.value = false;
    loadInFlight = false;
  }
};

const saveSamlSettings = async settings => {
  try {
    isSubmitting.value = true;

    if (isEnabled.value && formState.ssoUrl) {
      // Create or update settings based on existing id
      let response;
      if (id.value) {
        response = await samlSettingsAPI.update(settings);
      } else {
        response = await samlSettingsAPI.create(settings);
      }

      // Update local state with response data including fingerprint and id
      if (response?.data) {
        id.value = response.data.id;
        fingerprint.value = response.data.fingerprint || '';
        spEntityId.value = response.data.sp_entity_id || '';
        acsUrl.value = response.data.acs_url || '';
      }

      useAlert(t('SECURITY_SETTINGS.SAML.API.SUCCESS'));
    } else {
      // Disable/delete settings
      await samlSettingsAPI.delete();
      useAlert(t('SECURITY_SETTINGS.SAML.API.DISABLED'));
    }
  } catch (error) {
    // Handle backend validation errors
    if (error.response?.data?.errors) {
      const errorMessages = error.response.data.errors;
      const firstError = Array.isArray(errorMessages)
        ? errorMessages[0]
        : errorMessages;
      useAlert(firstError);
    } else {
      useAlert(t('SECURITY_SETTINGS.SAML.API.ERROR'));
    }
    throw error;
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  if (isSubmitting.value || isLoading.value || loadFailed.value) return;
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const mappings = Object.create(null);
  for (const row of roleMappings.value) {
    const group = row.group.trim();
    if (!group || Object.hasOwn(mappings, group) || !row.target) {
      useAlert(t('TEAM_LILITH.SAML.INVALID_MAPPING'));
      return;
    }
    mappings[group] = row.target.startsWith('custom:')
      ? { custom_role_id: Number(row.target.slice(7)) }
      : { role: row.target };
  }
  const settings = {
    sso_url: formState.ssoUrl,
    certificate: formState.certificate,
    idp_entity_id: formState.idpEntityId,
    role_mappings: mappings,
  };

  try { await saveSamlSettings(settings); } catch { /* Error is shown by saveSamlSettings. */ }
};

const toggleSaml = async () => {
  if (isSubmitting.value || isLoading.value || loadFailed.value) return;
  if (isEnabled.value) return;
  try {
    await saveSamlSettings({});
    await loadSamlSettings();
  } catch {
    isEnabled.value = true;
  }
};

onMounted(() => {
  loadSamlSettings();
});
</script>

<template>
  <SectionLayout
    :title="t('SECURITY_SETTINGS.SAML.TITLE')"
    :description="t('SECURITY_SETTINGS.SAML.NOTE')"
    :hide-content="!isEnabled || isLoading || loadFailed"
    class="max-w-2xl ltr:mr-auto rtl:ml-auto"
  >
    <template #headerActions>
      <div class="flex justify-end gap-2">
        <NextButton v-if="loadFailed" :disabled="isLoading || isSubmitting" :label="t('TEAM_LILITH.SAML.RETRY')" @click="loadSamlSettings" />
        <Switch
          v-model="isEnabled"
          :disabled="isLoading || isSubmitting || loadFailed"
          @change="toggleSaml"
        />
      </div>
    </template>

    <SamlInfoSection
      class="mb-5"
      :acs-url="acsUrl"
      :fingerprint="fingerprint"
      :sp-entity-id="spEntityId"
    />
    <SamlAttributeMap class="mb-5" />

    <form class="grid gap-5" @submit.prevent="handleSubmit">
      <WithLabel
        name="ssoUrl"
        :label="t('SECURITY_SETTINGS.SAML.SSO_URL.LABEL')"
        :help-message="t('SECURITY_SETTINGS.SAML.SSO_URL.HELP')"
        :has-error="v$.ssoUrl.$error"
        :error-message="ssoUrlError"
        required
      >
        <TextInput
          v-model="formState.ssoUrl"
          class="w-full"
          type="url"
          :placeholder="t('SECURITY_SETTINGS.SAML.SSO_URL.PLACEHOLDER')"
        />
      </WithLabel>

      <WithLabel
        name="idpEntityId"
        :label="t('SECURITY_SETTINGS.SAML.IDP_ENTITY_ID.LABEL')"
        :help-message="t('SECURITY_SETTINGS.SAML.IDP_ENTITY_ID.HELP')"
        :has-error="v$.idpEntityId.$error"
        :error-message="idpEntityIdError"
        required
      >
        <TextInput
          v-model="formState.idpEntityId"
          class="w-full"
          :placeholder="t('SECURITY_SETTINGS.SAML.IDP_ENTITY_ID.PLACEHOLDER')"
        />
      </WithLabel>

      <WithLabel
        name="certificate"
        :label="t('SECURITY_SETTINGS.SAML.CERTIFICATE.LABEL')"
        :help-message="t('SECURITY_SETTINGS.SAML.CERTIFICATE.HELP')"
        :has-error="v$.certificate.$error"
        :error-message="certificateError"
        required
      >
        <TextArea
          v-model="formState.certificate"
          class="w-full"
          rows="8"
          :placeholder="t('SECURITY_SETTINGS.SAML.CERTIFICATE.PLACEHOLDER')"
        />
      </WithLabel>

      <section class="grid gap-3">
        <h3>{{ t('TEAM_LILITH.SAML.GROUP_ROLES') }}</h3>
        <p class="text-sm text-n-slate-11">{{ t('TEAM_LILITH.SAML.GROUP_HELP') }}</p>
        <div v-for="(row, index) in roleMappings" :key="index" class="flex gap-2">
          <TextInput v-model="row.group" :placeholder="t('TEAM_LILITH.SAML.GROUP')" class="flex-1" />
          <select v-model="row.target" :aria-label="t('TEAM_LILITH.SAML.ROLE')" class="rounded border border-n-weak bg-n-solid-1 px-2">
            <option value="agent">{{ t('TEAM_LILITH.SAML.AGENT') }}</option>
            <option value="administrator">{{ t('TEAM_LILITH.SAML.ADMINISTRATOR') }}</option>
            <option v-for="role in customRoles" :key="role.id" :value="`custom:${role.id}`">{{ role.name }}</option>
            <option v-if="row.target?.startsWith('custom:') && !customRoles.some(role => `custom:${role.id}` === row.target)" :value="row.target" disabled>{{ t('TEAM_LILITH.SAML.MISSING_ROLE') }}</option>
          </select>
          <NextButton type="button" :label="t('TEAM_LILITH.SAML.REMOVE')" @click="roleMappings.splice(index, 1)" />
        </div>
        <NextButton type="button" :disabled="roleMappings.length >= 100" :label="t('TEAM_LILITH.SAML.ADD_GROUP')" @click="roleMappings.push({ group: '', target: 'agent' })" />
      </section>

      <div class="flex gap-2">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          :disabled="isLoading || isSubmitting || loadFailed"
          :label="t('SECURITY_SETTINGS.SAML.UPDATE_BUTTON')"
        />
      </div>
    </form>
  </SectionLayout>
</template>
